import { trackNewsletterAction } from '@/lib/subscribeFunnelAnalytics';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowLeft, ArrowRight } from 'lucide-react';
import { displaySlotToEmailParam, type DisplaySlot } from '@/lib/quiz/emailAnswerIntent';
import { registerPostCriticalCallback } from '@/lib/performance/postCriticalGate';
import { scheduleIdleTask } from '@/lib/performance/scheduleIdleTask';
import GameSkeleton from '@/components/GameSkeleton/GameSkeleton';
import NewsletterEmailChrome from './NewsletterEmailChrome';
import { getNewsletterEmailAnswers, NewsletterEmailQuestion } from './NewsletterEmailQuestion';
import { DEMO_QUESTION, DEMO_QUIZ_SLUG } from './newsletterDemoContent';
import type { NewsletterDemoTheme } from '@/lib/quiz/newsletterDemoThemes';

const DEMO_DOMAIN = 'atrivial.com';
const DEMO_CORRECT_ANSWER = getNewsletterEmailAnswers(DEMO_QUESTION).find(
  (answer) => answer.index === 0
);

const QUIZ_THEME_PRELOAD_DELAY_MS = 250;

const PHONE_FLAT_PROXIMITY_PX = 64;
const PHONE_FULL_TILT_DISTANCE_PX = 360;
const PHONE_MAX_ROTATE_X_DEG = 4;
const PHONE_MAX_ROTATE_Y_DEG = 7.25;
const PHONE_MAX_ROTATE_Z_DEG = 0.4;

type PhoneTilt = {
  rotateX: number;
  rotateY: number;
  rotateZ: number;
};

const FLAT_PHONE_TILT: PhoneTilt = {
  rotateX: 0,
  rotateY: 0,
  rotateZ: 0,
};

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function smootherStep(value: number) {
  const progress = clamp(value, 0, 1);
  return progress * progress * progress * (progress * (progress * 6 - 15) + 10);
}

function getPhoneTilt(
  clientX: number,
  clientY: number,
  phoneRect: DOMRect,
  viewportHeight: number
): PhoneTilt {
  const distanceX = Math.max(phoneRect.left - clientX, 0, clientX - phoneRect.right);
  const distanceY = Math.max(phoneRect.top - clientY, 0, clientY - phoneRect.bottom);
  const distanceFromDevice = Math.hypot(distanceX, distanceY);

  if (distanceFromDevice <= PHONE_FLAT_PROXIMITY_PX) {
    return FLAT_PHONE_TILT;
  }

  const phoneCenterX = phoneRect.left + phoneRect.width / 2;
  const phoneCenterY = phoneRect.top + phoneRect.height / 2;
  const pointerOffsetX = clientX - phoneCenterX;
  const pointerOffsetY = clientY - phoneCenterY;
  const pointerDistance = Math.hypot(pointerOffsetX, pointerOffsetY);

  if (pointerDistance === 0) {
    return FLAT_PHONE_TILT;
  }

  const tiltDepth = smootherStep(
    (distanceFromDevice - PHONE_FLAT_PROXIMITY_PX) /
      (PHONE_FULL_TILT_DISTANCE_PX - PHONE_FLAT_PROXIMITY_PX)
  );
  const directionX = pointerOffsetX / pointerDistance;
  const viewportBottom = Math.max(viewportHeight - 1, 1);
  const upwardProgress = 1 - clamp(clientY / viewportBottom, 0, 1);

  return {
    rotateX: upwardProgress * PHONE_MAX_ROTATE_X_DEG * tiltDepth,
    rotateY: directionX * PHONE_MAX_ROTATE_Y_DEG * tiltDepth,
    rotateZ: directionX * PHONE_MAX_ROTATE_Z_DEG * tiltDepth,
  };
}

function applyPhoneTilt(target: HTMLElement, tilt: PhoneTilt) {
  target.style.setProperty('--newsletter-demo-rotate-x', `${tilt.rotateX.toFixed(2)}deg`);
  target.style.setProperty('--newsletter-demo-rotate-y', `${tilt.rotateY.toFixed(2)}deg`);
  target.style.setProperty('--newsletter-demo-rotate-z', `${tilt.rotateZ.toFixed(2)}deg`);
}

function AnswerPromptContent({ direction }: { direction: 'right' | 'down' }) {
  const Icon = direction === 'right' ? ArrowRight : ArrowDown;

  return (
    <>
      <span>Select an answer</span>
      <span className="newsletter-demo-prompt-icon" aria-hidden="true">
        <Icon className="h-4 w-4 stroke-[2.5]" />
      </span>
    </>
  );
}

function NewsletterEmailSkeleton({ afterQuiz = false }: { afterQuiz?: boolean }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);
  const [lineCount, setLineCount] = useState(afterQuiz ? 0 : 3);
  const testId = afterQuiz
    ? 'newsletter-demo-email-skeleton-after'
    : 'newsletter-demo-email-skeleton';

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateLines = () => {
      const style = window.getComputedStyle(container);
      const lineHeight = Number.parseFloat(style.lineHeight);
      if (lineHeight > 0) {
        const titleSpace = titleRef.current
          ? titleRef.current.offsetHeight + (Number.parseFloat(style.rowGap) || 0)
          : 0;
        setLineCount(
          Math.max(0, Math.min(3, Math.floor((container.clientHeight - titleSpace) / lineHeight)))
        );
      }
    };
    updateLines();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateLines);
    observer?.observe(container);
    return () => observer?.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      className={`newsletter-demo-email-skeleton ${afterQuiz ? 'newsletter-demo-email-skeleton-after' : ''}`}
      aria-hidden="true"
    >
      {!afterQuiz && (
        <span
          ref={titleRef}
          data-testid="newsletter-demo-email-skeleton-title"
          className="newsletter-demo-email-skeleton-title"
        />
      )}
      <div data-testid={`${testId}-copy`} className="newsletter-demo-email-skeleton-copy">
        {Array.from({ length: lineCount }, (_, index) => (
          <span key={index} className="newsletter-demo-email-skeleton-line" />
        ))}
      </div>
    </div>
  );
}

function buildLiveQuizUrl(displaySlot?: DisplaySlot, themeId?: string) {
  const params = new URLSearchParams({
    view: 'iframe',
    origin: DEMO_DOMAIN,
    source: DEMO_DOMAIN,
    utm_source: DEMO_DOMAIN,
  });

  if (themeId) params.set('demo_theme', themeId);

  if (displaySlot !== undefined) {
    params.set('email_choice', displaySlotToEmailParam(displaySlot));
  }

  return `/quiz/${DEMO_QUIZ_SLUG}?${params.toString()}`;
}

function getBrowserDisplayUrl(displaySlot: DisplaySlot | null) {
  const choice =
    displaySlot === null ? '' : `?email_choice=${displaySlotToEmailParam(displaySlot)}`;
  return `atrivial.com/quiz/${DEMO_QUIZ_SLUG}${choice}`;
}

function canWarmDemoAssets() {
  const nav = navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
  };
  return (
    !nav.connection?.saveData &&
    nav.connection?.effectiveType !== '2g' &&
    nav.connection?.effectiveType !== 'slow-2g'
  );
}

function canAutoPreloadLiveQuiz() {
  if (typeof navigator === 'undefined') {
    return false;
  }

  if (
    typeof window.matchMedia === 'function' &&
    (window.matchMedia('(max-width: 767px)').matches ||
      window.matchMedia('(hover: none), (pointer: coarse)').matches)
  ) {
    return false;
  }

  return canWarmDemoAssets();
}

// The live Quiz needs this library even on mobile, where its full iframe stays
// on intent. Prepare code without constructing a client or changing auth/state.
function prepareLiveQuizLibrary() {
  void import('@supabase/supabase-js')
    .then(({ createClient }) => createClient)
    .catch(() => {
      // The requested Quiz retains its normal loading/recovery behavior.
    });
}

export default function NewsletterHeroDemo() {
  const [previewTheme, setPreviewTheme] = useState<NewsletterDemoTheme | null>(null);
  const themeChangeEpochRef = useRef(0);
  const themeResourcesRef = useRef<typeof import('@/lib/quiz/newsletterDemoThemes') | null>(null);
  const themeLoadRef = useRef<Promise<typeof import('@/lib/quiz/newsletterDemoThemes')> | null>(
    null
  );
  const themeFontsStartedRef = useRef(false);
  const mountedRef = useRef(true);
  const loadThemeResources = useCallback(() => {
    if (!themeLoadRef.current) {
      themeLoadRef.current = Promise.all([
        import('@/lib/quiz/newsletterDemoThemes'),
        import('./newsletter-demo-themes.css'),
      ])
        .then(([themes]) => {
          themeResourcesRef.current = themes;
          return themes;
        })
        .catch((error: unknown) => {
          themeLoadRef.current = null;
          throw error;
        });
    }
    return themeLoadRef.current;
  }, []);
  const warmThemes = useCallback(() => {
    void loadThemeResources()
      .then((themes) => {
        if (!mountedRef.current || themeFontsStartedRef.current) return;
        themeFontsStartedRef.current = true;
        void themes.warmNewsletterDemoThemeFonts(() => mountedRef.current);
      })
      .catch(() => {
        // Optional warming is silent; a click can retry the same loader.
      });
  }, [loadThemeResources]);

  useEffect(() => {
    mountedRef.current = true;
    let cancelIdle: (() => void) | undefined;
    const scheduleWarmup = () => {
      if (!canWarmDemoAssets()) return;
      cancelIdle = scheduleIdleTask(() => {
        if (!canWarmDemoAssets()) return;
        warmThemes();
        prepareLiveQuizLibrary();
      }, 1000);
    };
    if (document.readyState === 'complete') scheduleWarmup();
    else window.addEventListener('load', scheduleWarmup, { once: true });
    return () => {
      mountedRef.current = false;
      themeChangeEpochRef.current += 1;
      window.removeEventListener('load', scheduleWarmup);
      cancelIdle?.();
    };
  }, [warmThemes]);

  const changeTheme = async () => {
    const epoch = themeChangeEpochRef.current;
    // Once warm, update in this event, without another import/promise round trip.
    const themes = themeResourcesRef.current ?? (await loadThemeResources());
    warmThemes();
    // An answer clicked during loading continues with the style that was visible.
    if (epoch !== themeChangeEpochRef.current) return;
    setPreviewTheme((current) =>
      themes.nextNewsletterDemoTheme(current?.id, window.location.origin)
    );
  };
  const preloadedLiveQuizUrl = useMemo(
    () => buildLiveQuizUrl(undefined, previewTheme?.id),
    [previewTheme?.id]
  );
  const [emailLoadedAt, setEmailLoadedAt] = useState<Date | null>(null);
  // Client-only capture keeps prerendered HTML stable and the displayed time fixed.
  useEffect(() => {
    setEmailLoadedAt(new Date());
  }, []);
  const [selectedDisplaySlot, setSelectedDisplaySlot] = useState<DisplaySlot | null>(null);
  const [shouldPreloadQuiz, setShouldPreloadQuiz] = useState(false);
  const [quizFrameSrc, setQuizFrameSrc] = useState(preloadedLiveQuizUrl);
  const [quizFrameResetKey, setQuizFrameResetKey] = useState(0);
  const [quizFrameLoadEpoch, setQuizFrameLoadEpoch] = useState(0);
  const [quizFrameReady, setQuizFrameReady] = useState(false);
  const [selectionPillTop, setSelectionPillTop] = useState<number | null>(null);
  const sceneRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const emailMockRef = useRef<HTMLDivElement>(null);
  const correctAnswerButtonRef = useRef<HTMLButtonElement>(null);
  const cancelSelectionPillPositionUpdateRef = useRef<(() => void) | null>(null);
  const pendingPhonePointerRef = useRef<{ clientX: number; clientY: number } | null>(null);
  const quizFrameRef = useRef<HTMLIFrameElement>(null);
  const isQuizFrameReadyRef = useRef(false);
  const requestedQuizFrameUrlRef = useRef(preloadedLiveQuizUrl);
  const allowsPointerTiltRef = useRef(true);
  const isBrowserOpen = selectedDisplaySlot !== null;
  const isQuizFrameVisuallyReady = quizFrameReady;
  const browserDisplayUrl = getBrowserDisplayUrl(selectedDisplaySlot);
  const ensureQuizFramePreloaded = useCallback(() => {
    prepareLiveQuizLibrary();
    setShouldPreloadQuiz(true);
  }, []);
  const flattenPhoneTilt = useCallback(() => {
    pendingPhonePointerRef.current = null;
    const scene = sceneRef.current;
    if (scene) {
      applyPhoneTilt(scene, FLAT_PHONE_TILT);
    }
  }, []);
  const applyQuizFrameUrl = useCallback((targetUrl: string) => {
    const frameWindow = quizFrameRef.current?.contentWindow;
    if (!frameWindow || typeof window === 'undefined') {
      return false;
    }

    try {
      const absoluteTarget = new URL(targetUrl, window.location.origin);
      const currentFrameUrl = new URL(frameWindow.location.href);
      if (
        currentFrameUrl.origin !== window.location.origin ||
        currentFrameUrl.pathname !== absoluteTarget.pathname
      ) {
        return false;
      }

      const nextFrameUrl = `${absoluteTarget.pathname}${absoluteTarget.search}`;
      frameWindow.history.replaceState(frameWindow.history.state, '', nextFrameUrl);
      frameWindow.dispatchEvent(
        new PopStateEvent('popstate', { state: frameWindow.history.state })
      );
      return true;
    } catch {
      return false;
    }
  }, []);
  const handleOpenQuiz = useCallback(
    (displaySlot: DisplaySlot) => {
      trackNewsletterAction('hero_demo_answer');
      themeChangeEpochRef.current += 1;
      flattenPhoneTilt();
      ensureQuizFramePreloaded();
      setSelectedDisplaySlot(displaySlot);

      const targetUrl = buildLiveQuizUrl(displaySlot, previewTheme?.id);
      const themePreloadPending = requestedQuizFrameUrlRef.current !== preloadedLiveQuizUrl;
      requestedQuizFrameUrlRef.current = targetUrl;
      if (!themePreloadPending && isQuizFrameReadyRef.current && applyQuizFrameUrl(targetUrl)) {
        return;
      }

      isQuizFrameReadyRef.current = false;
      setQuizFrameReady(false);
      setQuizFrameSrc(targetUrl);
      if (themePreloadPending) {
        // The old document's ready marker cannot certify a newly selected theme.
        setQuizFrameResetKey((key) => key + 1);
      }
    },
    [
      applyQuizFrameUrl,
      ensureQuizFramePreloaded,
      flattenPhoneTilt,
      preloadedLiveQuizUrl,
      previewTheme?.id,
    ]
  );
  const handleSelectCorrectAnswer = useCallback(() => {
    if (DEMO_CORRECT_ANSWER) {
      handleOpenQuiz(DEMO_CORRECT_ANSWER.displaySlot);
    }
  }, [handleOpenQuiz]);
  const handleBackToNewsletter = useCallback(() => {
    trackNewsletterAction('hero_demo_return');
    setSelectedDisplaySlot(null);
    isQuizFrameReadyRef.current = false;
    requestedQuizFrameUrlRef.current = preloadedLiveQuizUrl;
    setQuizFrameReady(false);
    setQuizFrameSrc(preloadedLiveQuizUrl);
    setQuizFrameResetKey((currentKey) => currentKey + 1);
    setShouldPreloadQuiz(true);
  }, [preloadedLiveQuizUrl]);

  // Theme clicks update the email immediately. Restart the hidden Quiz only after
  // they settle, so a burst cannot repeatedly boot the whole player on the main thread.
  // Opening an answer cancels this preload and requests the latest theme immediately.
  useEffect(() => {
    if (isBrowserOpen || requestedQuizFrameUrlRef.current === preloadedLiveQuizUrl) return;
    const timeout = window.setTimeout(() => {
      requestedQuizFrameUrlRef.current = preloadedLiveQuizUrl;
      isQuizFrameReadyRef.current = false;
      setQuizFrameReady(false);
      setQuizFrameSrc(preloadedLiveQuizUrl);
      setQuizFrameResetKey((key) => key + 1);
    }, QUIZ_THEME_PRELOAD_DELAY_MS);
    return () => window.clearTimeout(timeout);
  }, [isBrowserOpen, preloadedLiveQuizUrl]);

  useEffect(() => {
    if (!isBrowserOpen || selectedDisplaySlot === null) return;

    const targetUrl = buildLiveQuizUrl(selectedDisplaySlot, previewTheme?.id);
    if (requestedQuizFrameUrlRef.current === targetUrl) return;
    requestedQuizFrameUrlRef.current = targetUrl;

    if (isQuizFrameReadyRef.current && applyQuizFrameUrl(targetUrl)) {
      return;
    }

    isQuizFrameReadyRef.current = false;
    setQuizFrameReady(false);
    setQuizFrameSrc(targetUrl);
  }, [applyQuizFrameUrl, isBrowserOpen, selectedDisplaySlot, previewTheme?.id]);

  useEffect(() => {
    if (!shouldPreloadQuiz) return;

    const iframe = quizFrameRef.current;
    if (!iframe) return;

    let cancelled = false;
    let readyFrame: number | null = null;
    let observer: MutationObserver | null = null;

    const readThemeRoot = () => {
      try {
        return iframe.contentDocument?.documentElement ?? null;
      } catch {
        return null;
      }
    };

    const confirmReadyTheme = () => {
      const themeRoot = readThemeRoot();
      if (!themeRoot || themeRoot.dataset.themeReady !== 'true') {
        return;
      }

      if (readyFrame !== null) {
        window.cancelAnimationFrame(readyFrame);
      }
      readyFrame = window.requestAnimationFrame(() => {
        const committedThemeRoot = readThemeRoot();
        if (cancelled || !committedThemeRoot || committedThemeRoot.dataset.themeReady !== 'true') {
          return;
        }
        setQuizFrameReady(true);
      });
    };

    const themeRoot = readThemeRoot();
    if (themeRoot && typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(confirmReadyTheme);
      observer.observe(themeRoot, {
        attributes: true,
        attributeFilter: ['data-theme', 'data-theme-ready'],
      });
    }
    confirmReadyTheme();

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (readyFrame !== null) {
        window.cancelAnimationFrame(readyFrame);
      }
    };
  }, [quizFrameLoadEpoch, quizFrameResetKey, quizFrameSrc, shouldPreloadQuiz]);

  const updateSelectionPillPosition = useCallback(() => {
    const scene = sceneRef.current;
    const correctAnswerButton = correctAnswerButtonRef.current;
    if (!scene || !correctAnswerButton) {
      return;
    }

    const sceneRect = scene.getBoundingClientRect();
    const answerRect = correctAnswerButton.getBoundingClientRect();
    const nextTop = answerRect.top + answerRect.height / 2 - sceneRect.top;
    setSelectionPillTop((currentTop) =>
      currentTop !== null && Math.abs(currentTop - nextTop) < 0.5 ? currentTop : nextTop
    );
  }, []);
  const scheduleSelectionPillPositionUpdate = useCallback(() => {
    if (cancelSelectionPillPositionUpdateRef.current) return;
    const updatePosition = () => {
      cancelSelectionPillPositionUpdateRef.current = null;
      const pointer = pendingPhonePointerRef.current;
      pendingPhonePointerRef.current = null;
      const scene = sceneRef.current;
      const phone = phoneRef.current;
      if (pointer && scene && phone && allowsPointerTiltRef.current) {
        applyPhoneTilt(
          scene,
          getPhoneTilt(
            pointer.clientX,
            pointer.clientY,
            phone.getBoundingClientRect(),
            window.innerHeight
          )
        );
      }
      updateSelectionPillPosition();
    };
    if (typeof window.requestAnimationFrame === 'function') {
      const frame = window.requestAnimationFrame(updatePosition);
      cancelSelectionPillPositionUpdateRef.current = () => window.cancelAnimationFrame(frame);
    } else {
      const timeout = window.setTimeout(updatePosition, 0);
      cancelSelectionPillPositionUpdateRef.current = () => window.clearTimeout(timeout);
    }
  }, [updateSelectionPillPosition]);

  useEffect(
    () => () => {
      cancelSelectionPillPositionUpdateRef.current?.();
      cancelSelectionPillPositionUpdateRef.current = null;
    },
    []
  );

  useLayoutEffect(() => {
    if (isBrowserOpen) {
      return;
    }

    const emailMock = emailMockRef.current;
    if (!emailMock) {
      return;
    }

    emailMock.scrollTop = Math.max(emailMock.scrollHeight - emailMock.clientHeight, 0);
    updateSelectionPillPosition();
  }, [isBrowserOpen, previewTheme?.id, updateSelectionPillPosition]);

  const resetTilt = useCallback((target: HTMLElement) => {
    target.style.removeProperty('--newsletter-demo-rotate-x');
    target.style.removeProperty('--newsletter-demo-rotate-y');
    target.style.removeProperty('--newsletter-demo-rotate-z');
  }, []);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || typeof window.matchMedia !== 'function') {
      return;
    }

    const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const mobileLayoutQuery = window.matchMedia('(max-width: 767px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncPointerTiltPreference = () => {
      allowsPointerTiltRef.current =
        finePointerQuery.matches && !mobileLayoutQuery.matches && !reducedMotionQuery.matches;

      if (allowsPointerTiltRef.current) {
        resetTilt(scene);
      } else {
        applyPhoneTilt(scene, FLAT_PHONE_TILT);
      }
    };

    syncPointerTiltPreference();
    finePointerQuery.addEventListener?.('change', syncPointerTiltPreference);
    mobileLayoutQuery.addEventListener?.('change', syncPointerTiltPreference);
    reducedMotionQuery.addEventListener?.('change', syncPointerTiltPreference);

    return () => {
      finePointerQuery.removeEventListener?.('change', syncPointerTiltPreference);
      mobileLayoutQuery.removeEventListener?.('change', syncPointerTiltPreference);
      reducedMotionQuery.removeEventListener?.('change', syncPointerTiltPreference);
    };
  }, [resetTilt]);

  useEffect(() => {
    const handlePointerMove = (event: globalThis.PointerEvent) => {
      const scene = sceneRef.current;
      const phone = phoneRef.current;
      if (!scene || !phone) {
        return;
      }

      if (isBrowserOpen) {
        pendingPhonePointerRef.current = null;
        applyPhoneTilt(scene, FLAT_PHONE_TILT);
        scheduleSelectionPillPositionUpdate();
        return;
      }

      if (event.pointerType !== 'mouse' || !allowsPointerTiltRef.current) {
        pendingPhonePointerRef.current = null;
        applyPhoneTilt(scene, FLAT_PHONE_TILT);
        scheduleSelectionPillPositionUpdate();
        return;
      }

      // Several pointer events can arrive before the browser can display a frame.
      // Read geometry and apply only the latest position in the shared frame.
      pendingPhonePointerRef.current = { clientX: event.clientX, clientY: event.clientY };
      scheduleSelectionPillPositionUpdate();
    };
    const handleWindowBlur = () => {
      pendingPhonePointerRef.current = null;
      const scene = sceneRef.current;
      if (!scene) {
        return;
      }

      if (isBrowserOpen) {
        applyPhoneTilt(scene, FLAT_PHONE_TILT);
      } else if (allowsPointerTiltRef.current) {
        resetTilt(scene);
      }
      scheduleSelectionPillPositionUpdate();
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [isBrowserOpen, resetTilt, scheduleSelectionPillPositionUpdate]);

  useEffect(() => {
    if (shouldPreloadQuiz) return;
    let cancelIdle: (() => void) | undefined;
    const unregister = registerPostCriticalCallback(
      'newsletter-demo-quiz-preload',
      () => {
        if (canAutoPreloadLiveQuiz()) {
          cancelIdle = scheduleIdleTask(() => {
            if (canAutoPreloadLiveQuiz()) setShouldPreloadQuiz(true);
          }, 1000);
        }
      },
      'low'
    );
    return () => {
      unregister();
      cancelIdle?.();
    };
  }, [shouldPreloadQuiz]);

  useEffect(() => {
    if (isBrowserOpen) {
      setSelectionPillTop(null);
      return;
    }

    updateSelectionPillPosition();

    const emailMock = emailMockRef.current;
    emailMock?.addEventListener('scroll', updateSelectionPillPosition, { passive: true });
    window.addEventListener('resize', updateSelectionPillPosition);

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(updateSelectionPillPosition);
    [sceneRef.current, phoneRef.current, emailMock, correctAnswerButtonRef.current].forEach(
      (element) => {
        if (element) {
          resizeObserver?.observe(element);
        }
      }
    );

    return () => {
      emailMock?.removeEventListener('scroll', updateSelectionPillPosition);
      window.removeEventListener('resize', updateSelectionPillPosition);
      resizeObserver?.disconnect();
    };
  }, [isBrowserOpen, previewTheme?.id, updateSelectionPillPosition]);

  return (
    <div
      ref={sceneRef}
      data-testid="newsletter-demo-scene"
      className="newsletter-demo-scene relative z-40 mx-auto w-full max-w-[22rem] pt-3 pb-8 sm:pt-7 xl-wide:mr-0 xl-wide:-mb-12 xl-wide:pt-0 xl-wide:pb-0"
    >
      <div className="newsletter-demo-depth" aria-hidden="true" />

      {!isBrowserOpen ? (
        <button
          type="button"
          data-testid="newsletter-demo-desktop-prompt"
          aria-label="Select the correct answer"
          onClick={handleSelectCorrectAnswer}
          className={`newsletter-demo-pill newsletter-demo-answer-prompt pointer-events-auto absolute left-0 z-20 hidden min-h-11 cursor-pointer touch-manipulation items-center gap-2 rounded-full py-1.5 pr-1.5 pl-4 text-[13px] font-semibold whitespace-nowrap focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-primary xl-wide:inline-flex ${
            selectionPillTop === null ? 'opacity-0' : 'opacity-100'
          }`}
          style={{ top: selectionPillTop === null ? 0 : selectionPillTop }}
        >
          <AnswerPromptContent direction="right" />
        </button>
      ) : null}

      <div
        ref={phoneRef}
        data-testid="newsletter-demo-phone"
        data-preview-theme={previewTheme?.id}
        className="newsletter-demo-phone bg-nl-phone text-nl-phone relative rounded-[2rem] p-[6px] shadow-nl-pop"
        style={{
          ...previewTheme?.phoneStyle,
          transform: isBrowserOpen ? 'none' : undefined,
        }}
        onPointerDownCapture={flattenPhoneTilt}
        onTransitionEnd={(event) => {
          if (event.propertyName === 'transform') {
            updateSelectionPillPosition();
          }
        }}
      >
        <div className="relative z-10 h-full overflow-hidden rounded-[1.65rem] bg-raised">
          {isBrowserOpen || shouldPreloadQuiz ? (
            <div
              data-testid="newsletter-demo-browser"
              aria-hidden={!isBrowserOpen}
              className={`bg-primary text-glyph-primary ${
                isBrowserOpen
                  ? 'relative flex h-full flex-col'
                  : 'pointer-events-none invisible absolute inset-0 flex h-full flex-col'
              }`}
            >
              <div className="flex h-11 shrink-0 items-center gap-2 border-b border-outline-default bg-raised px-2">
                <button
                  type="button"
                  aria-label="Back to newsletter"
                  title="Back to newsletter"
                  onClick={handleBackToNewsletter}
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-glyph-secondary transition hover:bg-subtle hover:text-glyph-primary"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                </button>
                <div className="min-w-0 flex-1 truncate rounded-full border border-outline-default bg-subtle px-3 py-1.5 font-mono text-[10px] text-glyph-secondary">
                  {browserDisplayUrl}
                </div>
              </div>

              <div className="relative min-h-0 flex-1 overflow-hidden bg-primary">
                <iframe
                  key={quizFrameResetKey}
                  ref={quizFrameRef}
                  title="Atrivial live quiz demo"
                  src={quizFrameSrc}
                  sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms"
                  referrerPolicy="no-referrer-when-downgrade"
                  aria-hidden={!isBrowserOpen || !isQuizFrameVisuallyReady}
                  onLoad={() => {
                    isQuizFrameReadyRef.current = true;
                    setQuizFrameLoadEpoch((epoch) => epoch + 1);
                    try {
                      if (
                        quizFrameRef.current?.contentDocument?.documentElement.dataset
                          .themeReady === 'true'
                      ) {
                        setQuizFrameReady(true);
                      }
                    } catch {
                      // The observer handles same-origin readiness; cross-origin frames stay covered.
                    }
                  }}
                  className={`absolute inset-0 block h-full w-full border-0 bg-primary ${
                    isQuizFrameVisuallyReady ? 'opacity-100' : 'pointer-events-none opacity-0'
                  }`}
                />
                {isBrowserOpen && !isQuizFrameVisuallyReady ? (
                  <div
                    data-testid="newsletter-demo-quiz-skeleton"
                    className="gameplay-scope absolute inset-0 overflow-hidden bg-primary px-4 py-8"
                    data-gameplay-scope="true"
                  >
                    <GameSkeleton mode="quiz" showGenerationTag={false} />
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {!isBrowserOpen ? (
            <div
              data-testid="newsletter-demo-email"
              className="flex h-full flex-col overflow-hidden bg-raised text-glyph-primary"
            >
              <NewsletterEmailChrome
                sentAt={emailLoadedAt}
                onChangeTheme={changeTheme}
                onWarmThemes={warmThemes}
              />

              <div
                ref={emailMockRef}
                data-testid="newsletter-demo-email-body"
                className="newsletter-demo-email-body flex min-h-0 flex-1 flex-col overflow-y-auto bg-subtle"
              >
                <NewsletterEmailSkeleton />

                <NewsletterEmailQuestion
                  question={DEMO_QUESTION}
                  onAnswerSelect={(answer) => handleOpenQuiz(answer.displaySlot)}
                  getAnswerLabel={(text) => `Open live quiz with answer ${text}`}
                  onAnswerIntent={ensureQuizFramePreloaded}
                  correctAnswerButtonRef={correctAnswerButtonRef}
                  prompt={
                    <button
                      type="button"
                      data-testid="newsletter-demo-mobile-prompt"
                      aria-label="Select the correct answer"
                      onClick={handleSelectCorrectAnswer}
                      className="newsletter-demo-mobile-prompt newsletter-demo-answer-prompt mt-3 inline-flex min-h-11 touch-manipulation items-center gap-2 rounded-lg py-1.5 pr-1.5 pl-3 font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-primary xl-wide:hidden"
                    >
                      <AnswerPromptContent direction="down" />
                    </button>
                  }
                />
                <NewsletterEmailSkeleton afterQuiz />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
